import { useEffect, useRef, useState } from 'react';
import { miscApi } from '../api/miscApi';

// Human-verification widget for the unauthenticated forms.
//
// Provider-agnostic: Cloudflare Turnstile, hCaptcha and reCAPTCHA v2 all
// expose the same explicit-render API — `render(el, { sitekey, callback })`
// — so one component covers whichever the server is configured for. The
// provider + public site key come from /api/media/config, so switching
// providers is a server env change with no redeploy of this bundle.
//
// Renders NOTHING when no provider is configured, and the server's
// matching middleware is a pass-through in that case, so the forms keep
// working end to end before any CAPTCHA is set up.

const PROVIDERS = {
  turnstile: {
    src: 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit',
    global: 'turnstile'
  },
  hcaptcha: {
    src: 'https://js.hcaptcha.com/1/api.js?render=explicit',
    global: 'hcaptcha'
  },
  recaptcha: {
    src: 'https://www.google.com/recaptcha/api.js?render=explicit',
    global: 'grecaptcha'
  }
};

const loaders = new Map();
function loadProvider(key) {
  const spec = PROVIDERS[key];
  if (!spec) return Promise.resolve(null);
  if (loaders.has(key)) return loaders.get(key);
  const p = new Promise((resolve) => {
    if (window[spec.global]) { resolve(window[spec.global]); return; }
    const script = document.createElement('script');
    script.src = spec.src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      // grecaptcha exposes .ready(); the others are usable immediately.
      const api = window[spec.global];
      if (api && typeof api.ready === 'function') api.ready(() => resolve(api));
      else resolve(api || null);
    };
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  loaders.set(key, p);
  return p;
}

export default function CaptchaGate({ onToken, onError }) {
  const boxRef = useRef(null);
  const widgetRef = useRef(null);
  const [config, setConfig] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    miscApi.mediaConfig()
      .then((d) => { if (alive) setConfig(d?.captcha || null); })
      .catch(() => { if (alive) setConfig(null); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const provider = config?.provider;
    const siteKey = config?.siteKey;
    if (!provider || !siteKey || !PROVIDERS[provider] || !boxRef.current) return undefined;

    let alive = true;
    loadProvider(provider).then((api) => {
      if (!alive || !api || !boxRef.current) { if (alive) setFailed(true); return; }
      try {
        // Guard against StrictMode's double-invoke rendering two widgets.
        if (widgetRef.current !== null) return;
        widgetRef.current = api.render(boxRef.current, {
          sitekey: siteKey,
          callback: (token) => onToken?.(token),
          'expired-callback': () => onToken?.(''),
          'error-callback': () => { setFailed(true); onError?.(); }
        });
      } catch {
        setFailed(true);
      }
    });
    return () => { alive = false; };
  }, [config, onToken, onError]);

  if (!config?.provider || !config?.siteKey) return null;

  return (
    <div className="form-row">
      <div ref={boxRef} />
      {failed ? (
        <span className="hint" style={{ color: 'var(--warning)' }}>
          Verification could not load. Disable any ad/script blocker for this page and refresh.
        </span>
      ) : null}
    </div>
  );
}

// Hidden decoy input. Real users never see or fill it; naive bots fill
// every field they find and get rejected server-side. Rendered inside
// each auth form alongside CaptchaGate.
export function HoneypotField({ value, onChange }) {
  return (
    <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
      <label htmlFor="nickname">Leave this field empty</label>
      <input
        id="nickname"
        name="nickname"
        type="text"
        tabIndex={-1}
        autoComplete="off"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}
